/* eslint-disable react-hooks/rules-of-hooks */
import { HTTP_STATUS } from "@api/constant";
import GEVariant1 from "@components/Layouts/GeneralElection/GEVariant1";
import GEVariant2 from "@components/Layouts/GeneralElection/GEVariant2";
import GEVariant3 from "@components/Layouts/GeneralElection/GEVariant3";
import GEVariant4 from "@components/Layouts/GeneralElection/GEVariant4";
import { BANNER_CONFIG, BannerEvents } from "@config/BannerConfigConstants";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import { APP_VIEW_COOKIE } from "@header/HeaderMenuConstants";
import { SpecialHeader } from "@header/SpecialHeader";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { Route } from "@route-types/CustomPages/+types/GePage";
import fetchGeData, {
  GEPayloadResponseType,
} from "@routes/CustomPages/Ge.server";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags, MetaTagsProps } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import Cookies from "js-cookie";
import { ReactElement } from "react";
import { useSearchParams } from "react-router";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    GEPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function GePage(): ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    GEPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const [searchParams] = useSearchParams();
  const mobwebview = searchParams.get("mobwebview");
  if (mobwebview && mobwebview == "true") {
    Cookies.set(APP_VIEW_COOKIE, "true", {
      path: "/",
      SameSite: "None",
      Secure: true,
    });
  } else {
    Cookies.set(APP_VIEW_COOKIE, "false", {
      path: "/",
      expires: new Date(0), // Thu, 01 Jan 1970 00:00:00 UTC
    });
  }
  const mobwebviewCookie = Cookies.get(APP_VIEW_COOKIE);
  const mobwebviewBool = mobwebviewCookie == "true";

  const {
    tagAlias,
    geRecordedVideo,
    geSTWidget,
    geGovTechWidget,
    geLiveBlog,
    geVariant,
  } = dataLoaderResponse.payload;
  const keywordArticles = dataLoaderResponse.payload.data;
  const adsSettings: AdsSettings = useGlobalAdSetting();
  const tagName = BANNER_CONFIG[BannerEvents.GENERAL_ELECTION].title;
  const showBreadcrumbs = false;
  const showTitle = false;
  const pageAdTargetValue = PageAdTargetingTypeEnum.TAG;
  const sectionName = "general-election-2025";

  return (
    <MainContainer
      sectionName={sectionName}
      mobwebview={mobwebviewBool}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adsSettings}
    >
      <SpecialHeader title={tagName} />

      {(() => {
        switch (geVariant) {
          case "boundry_change":
            return (
              <GEVariant1
                geGovTechWidget={geGovTechWidget}
                keywordArticles={keywordArticles}
                tagAlias={tagAlias}
                sectionName={sectionName}
                pageAdTargetValue={pageAdTargetValue}
                showBreadcrumbs={showBreadcrumbs}
                showTitle={showTitle}
                tagName={tagName}
              />
            );
          case "parliment_desolve":
            return (
              <GEVariant2
                geGovTechWidget={geGovTechWidget}
                geLiveBlog={geLiveBlog}
                keywordArticles={keywordArticles}
                tagAlias={tagAlias}
                sectionName={sectionName}
                pageAdTargetValue={pageAdTargetValue}
                showBreadcrumbs={showBreadcrumbs}
                showTitle={showTitle}
                tagName={tagName}
              />
            );
          case "nomination":
            return (
              <GEVariant3
                geGovTechWidget={geGovTechWidget}
                geLiveBlog={geLiveBlog}
                geRecordedVideo={geRecordedVideo}
                keywordArticles={keywordArticles}
                tagAlias={tagAlias}
                sectionName={sectionName}
                pageAdTargetValue={pageAdTargetValue}
                showBreadcrumbs={showBreadcrumbs}
                showTitle={showTitle}
                tagName={tagName}
              />
            );
          case "polling_results":
            return (
              <GEVariant4
                geGovTechWidget={geGovTechWidget}
                geLiveBlog={geLiveBlog}
                geRecordedVideo={geRecordedVideo}
                geSTWidget={geSTWidget}
                keywordArticles={keywordArticles}
                tagAlias={tagAlias}
                sectionName={sectionName}
                pageAdTargetValue={pageAdTargetValue}
                showBreadcrumbs={showBreadcrumbs}
                showTitle={showTitle}
                tagName={tagName}
              />
            );
          default:
            return null;
        }
      })()}
    </MainContainer>
  );
}

export async function loader() {
  const data = await fetchGeData();

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };
  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<GEPayloadResponseType | null, string>
  >;
}
